import { v4 as uuidv4 } from 'uuid';

import React from 'react';
import { some } from 'lodash';

import * as FormProperties from './properties-config';
import * as FormTypes from './index';
import * as Validators from './validators';
import { getCookie } from 'src/utils/common.util';

export { getFieldPropertyValues };
const fieldTypes = {
  'Section Break': {
    properties: FormProperties.Section,
    component: FormTypes.FormFieldSection
  },
  'Date Picker': {
    properties: FormProperties.Date,
    component: FormTypes.FormFieldDate,
    validators: Validators.DatePicker
  },
  Time: { properties: FormProperties.Time, component: FormTypes.FormFieldTime },
  Number: {
    properties: FormProperties.Number,
    component: FormTypes.FormFieldNumber,
    validators: Validators.Number
  },
  'Short Text Entry': {
    properties: FormProperties.ShortText,
    component: FormTypes.FormFieldShortText,
    validators: Validators.ShortTextEntry
  },
  'Long Text Entry': {
    properties: FormProperties.LongText,
    component: FormTypes.FormFieldLongText,
    validators: Validators.LongTextEntry
  },
  Dropdown: {
    properties: FormProperties.Dropdown,
    component: FormTypes.FormFieldDropdown,
    validators: Validators.Dropdown
  },
  'Single Choice': {
    properties: FormProperties.Single,
    component: FormTypes.FormFieldSingle
  },
  'Multiple Choice': {
    properties: FormProperties.Multiple,
    component: FormTypes.FormFieldMultiple
  },
  Spinner: {
    properties: FormProperties.Spinner,
    component: FormTypes.FormFieldSpinner,
    validators: Validators.Spinner
  },
  Signature: {
    properties: FormProperties.Signature,
    component: FormTypes.FormFieldSignature,
    preventOverride: true
  },
  Submit: {
    properties: FormProperties.Submit,
    component: FormTypes.FormFieldSubmit
  },
  Sex: {
    properties: FormProperties.Sex,
    component: FormTypes.FormFieldGender,
    validators: Validators.Dropdown
  },
  SSN: {
    properties: FormProperties.SocialSecurityNumber,
    component: FormTypes.FormFieldSSN,
    validators: Validators.SSN,
    preventOverride: true
  },
  'Page Break': { properties: null, component: FormTypes.FormFieldPage },
  'Print Page Break': {
    properties: null,
    component: () => <p style={{ pageBreakBefore: 'always' }} />
  },
  Address: { properties: null, component: FormTypes.FormFieldAddress },
  SubFields: { properties: null, component: FormTypes.FormFieldSubFields },
  default: { properties: null, component: FormTypes.FormFieldDefault },
  'Email Address': { validators: Validators.EmailAddress },
  Birthdate: { validators: Validators.Birthdate },
  'Description Area': {
    properties: FormProperties.DescriptionArea,
    component: FormTypes.FormFieldDescriptionArea
  },
  'Appointment Booking': {
    properties: FormProperties.AppointmentBooking,
    component: FormTypes.FormFieldAppointmentBooking
  },
  Patient: {
    properties: FormProperties.Dropdown,
    component: FormTypes.FormFieldPatientDropdown,
    validators: Validators.Dropdown
  },
  'Mobile Consent': {
    properties: null,
    component: FormTypes.FormFieldMobileConsent,
    validators: Validators.PhoneNumber,
  },
  'Patient Attachment': {
    properties: FormProperties.PatientAttachment,
    component: FormTypes.FormFieldPatientAttachment
  },
  'Sliding Scale': { properties: FormProperties.SlidingScale, component: FormTypes.FormFieldSlidingScale },
  'Responsible Party': { properties: null, component: FormTypes.FormFieldResponsibleParty },
};

/**
 *  Shared methods for all form-field-types
 */
export const getSpecificProps = (field) => {
  let Properties;
  if (['Sex', 'SSN'].includes(field.display_name))
    Properties = fieldTypes[field.display_name].properties;
  else if (field.display_name !== 'Birthdate' && field.is_basic)
    Properties = [];
  else if (field.type_name === 'Date Picker')
    Properties = fieldTypes[field.type_name].properties.filter(([key, prop]) =>
      field.is_basic ? !prop.dateTimeOnly : true
    );
  else if (fieldTypes.hasOwnProperty(field.type_name))
    Properties = fieldTypes[field.type_name].properties;
  else Properties = fieldTypes.default.properties;
  return Properties;
};

export const getComponent = (field, props) => {
  let Component;
  if (
    ['Address', 'Sex', 'SSN', 'Appointment Booking', 'Patient', 'Mobile Consent', 'Responsible Party'].includes(
      field.display_name
    )
  ) {
    Component = fieldTypes[field.display_name].component;
  }
  else if (field.fields && field.type_name !== 'Section Break')
    Component = fieldTypes.SubFields.component;
  else if (fieldTypes.hasOwnProperty(field.type_name))
    Component = fieldTypes[field.type_name].component;
  else Component = fieldTypes.default.component;

  return <Component {...props} validationMessage={field.validationMessage} />;
};

export const getMapFromArray = (arr) =>
  arr instanceof Map ? arr : new Map(arr);

/**
 * getFieldProperties returns a Map object for each @types value passed
 * in that is found in the @field properties object.
 *
 * @param {object} field            The field we're requesting the properties for
 * @param {(string|string[])} types A string or array of strings of the type(s) of properties we want returned
 *                                  E.g.: ['common', 'specific']
 * @return {object}                 {<key-for-each-@types>: [<array>]}
 */
export const getFieldProperties = (field, types) => {
  const properties = field.properties || {};
  const result = {};
  if (typeof types === 'string') types = [types];
  types.forEach((type) => {
    if (properties.hasOwnProperty(type))
      result[type] = getMapFromArray(properties[type]);
  });
  return result;
};

export const hasSubfields = (field) => {
  return !!(field.fields && field.fields.length > 0);
};

export const { Messages } = Validators;

export const getFieldValidators = (field) =>
  getFieldByType(field).validators || [];

const getFieldByType = (field) => {
  const fieldType = fieldTypes[field.display_name || field.type_name];
  return fieldType || {};
};

export const getFieldValue = (field) => {
  if (Array.isArray(field.value) && !isArrayWithObjects(field.value)) {
    return field.value.join('');
  }
  return field.value;
};

const isArrayWithObjects = (arr) =>
  arr.find((item) => typeof item === 'object');

// browser safe HTTP request (GET/POST)
export const httpRequest = function (url, callback, options = {}) {
  const xhr = new XMLHttpRequest();
  let body;

  if (!options.method) options.method = 'GET';

  xhr.open(options.method, url, true);
  xhr.onreadystatechange = handler;
  xhr.responseType = 'json';
  const token = getCookie('token');

  xhr.setRequestHeader('Authorization', `Bearer ${token}`);

  if (options.method === 'POST' && options.body) {
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    body = `json=${encodeURIComponent(JSON.stringify(options.body))}`;
  }

  xhr.send(body);

  function handler() {
    if (this.readyState === this.DONE) {
      if (this.status === 200) callback(null, this.response);
      else
        callback(
          new Error(`${url} request failed with status: [${this.status}]`)
        );
    }
  }
};

export const convertStringTo = (str, replacement) => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    result += replacement;
  }
  return result;
};

export const replaceAt = (str, index, character) =>
  `${str.substr(0, index)}${character}${str.substr(index + character.length)}`;

export const setNewFieldDefaults = (newField) => {
  setCommonPropertyDefaults(newField);
  setSpecificPropertyDefaults(newField);
  setSubfieldPropertyDefaults(newField);
};

const setCommonPropertyDefaults = (field) => {
  let propMap;

  // default 'label' property to this field's display_name (only if not a "Section Break" or "Page Break" type field)
  if (['Section Break', 'Page Break'].includes(field.type_name)) return;

  propMap = getMapFromArray(field.properties.common);
  let { value, ...rest } = propMap.get('label');

  value = field.display_name;

  propMap.set('label', { value, ...rest });
  field.properties.common = [...propMap];
};

const setSpecificPropertyDefaults = (field) => {
  let propMap;

  // iterate field-specific properties and set values for any with default values defined
  if (!field.properties.specific) return;

  propMap = getMapFromArray(field.properties.specific);
  propMap.forEach((val, key) => {
    let { value, defaultValue, ...rest } = propMap.get(key);

    if (val.hasOwnProperty('defaultValue') && val.hasOwnProperty('value')) {
      value = defaultValue;
      propMap.set(key, { value, defaultValue, ...rest });
    } else if (val.hasOwnProperty('value') && Array.isArray(val.value)) {
      // some properties might have an array of values, which themselves
      // have "sub-properties" (of type 'object') which might have
      // default values that should be set
      const newValue = [...value.filter((item) => typeof item[1] === 'object')];

      newValue.forEach(([itemKey, itemValue], index) => {
        if (
          itemValue.hasOwnProperty('defaultValue') &&
          itemValue.hasOwnProperty('value')
        ) {
          const newItemValue = { ...itemValue, value: itemValue.defaultValue };

          newValue[index] = [itemKey, newItemValue];
          propMap.set(key, { value: newValue, defaultValue, ...rest });
        }
      });
    }
  });

  field.properties.specific = [...propMap];
};

const setSubfieldPropertyDefaults = (field) => {
  // set default 'sublabel' property values if this field has subfields
  if (!hasSubfields(field)) return;

  field.fields.forEach((subfield, index) => {
    const subfieldPropMap = getMapFromArray(subfield.properties);
    let { value, ...rest } = subfieldPropMap.get('sublabel');
    value = subfield.display_name;
    subfieldPropMap.set('sublabel', { value, ...rest });
    field.fields[index].properties = [...subfieldPropMap];
  });
};

export const mapFieldProps = (field) => {
  // if this is NOT a section or page break, add default (common) properties to this field
  if (!['Section Break', 'Page Break'].includes(field.type_name))
    field.properties = { common: [...FormProperties.Common] };

  // map subfield properties, if has subfields
  mapSubfieldProps(field);

  // map field specific properties base on field type
  const specificProps = getSpecificProps(field);
  if (specificProps)
    field.properties = {
      ...field.properties,
      specific: [...specificProps]
    };
};

const mapSubfieldProps = (field) => {
  if (!hasSubfields(field)) return;

  field.fields.forEach((subfield) => {
    // add uuid to subfield
    subfield.uuid = uuidv4();

    const dynamicProps = getMapFromArray(
      FormProperties.Common.filter((prop) => prop[1].isDynamic)
    );

    if (dynamicProps.has('isVisible')) {
      // set default visible property value for this sub-field
      let { value, ...rest } = dynamicProps.get('isVisible');

      value = subfield.is_visible;
      dynamicProps.set('isVisible', { value, ...rest });
    }

    // add properties to sub-field
    subfield.properties = [...dynamicProps];
  });
};

export const groupFields = (fields = []) => {
  // - reduce "grouped" fields down to their category name
  // - "grouped" fields should have children array of sub fields
  const groups = {};

  return fields
    .map((field) => {
      if (field.group_name === 'Ungrouped') return field;

      if (!groups[field.group_name]) {
        groups[field.group_name] = {
          id: field.id,
          form_field_group_id: field.form_field_group_id,
          display_name: field.group_name,
          sort_order: field.sort_order,
          fields: [field]
        };
        return groups[field.group_name];
      }

      groups[field.group_name].fields.push(field);
    })
    .filter((field) => field) // filter out undefined fields from .map()
    .sort((a, b) => a.sort_order - b.sort_order);
};

export const createSubmitButton = () => {
  const submit = {
    uuid: uuidv4(),
    type_name: 'Submit',
    display_name: 'Submit'
  };
  mapFieldProps(submit);
  setNewFieldDefaults(submit);
  return submit;
};

export const createConsentCheckbox = () => {
  const consent = {
    uuid: uuidv4(),
    form_field_type_id: 11,
    type_name: 'Checkbox',
    display_name: 'Mobile Consent'
  };
  mapFieldProps(consent);
  setNewFieldDefaults(consent);
  return consent;
};

export const fieldIsType = (field, type, comparisonAttribute = 'type_name') => {
  return field?.[comparisonAttribute] === type;
};

export const preventOverride = (field) =>
  !!getFieldByType(field).preventOverride;

export const setFieldPropertyValue = (
  field,
  propertyName,
  value,
  type = 'common'
) => {
  if (!field.properties || !field.properties[type]) return;

  const properties = getMapFromArray(getFieldProperties(field, type)[type]);
  const { value: currentValue, ...rest } = properties.get(propertyName);

  properties.set(propertyName, { value, ...rest });
  field.properties[type] = [...properties];
};



export const getQueryString = () => {
  return window.location.search.substring(1);
};

const getKeyValuePairSafe = (key, value) => {
  return {
    key: `${key}`.toLowerCase().trim(),
    value: `${value}`.trim(),
  }
};

export const getCookiesAsKeyValuePair = () => {
  const cookieStrings = document.cookie.trim().split(';');
  return cookieStrings.map(cookie => {
    const cookieSplit = cookie.trim().split('=');
    return getKeyValuePairSafe(cookieSplit[0], decodeURIComponent(cookieSplit[1]));
  });
};

const getParametersArrayFromQuery = (query) => {
  if (!query) {
    return [];
  }

  const params = new URLSearchParams(query);

  return Array.from(params.entries())
    .map(([key, value]) => {
      return getKeyValuePairSafe(key, value);
    });
};

export const getQueryStringParameters = () => {
  const query = getQueryString();

  return getParametersArrayFromQuery(query);
};

export const UTM_PARAMETER_PREFIX_REGEX = /^utm_/i;

const filterParametersToUTM = (parameters) => {
  const filteredParameters = {};

  parameters
    .filter(p => UTM_PARAMETER_PREFIX_REGEX.test(p.key))
    .forEach(({ key, value }) => {
      filteredParameters[key.toLowerCase()] = value;
    });

  return filteredParameters;
};

export const getUTMQueryStringParameters = () => {
  const parameters = getQueryStringParameters();

  return filterParametersToUTM(parameters);
};

export const getUTMCookieParameters = () => {
  const parameters = getCookiesAsKeyValuePair();

  return filterParametersToUTM(parameters);
};

export const GoogleReserveQueryParamKeys = {
  accountId: 'legworkaccountid',
  token: 'rwg_token',
};

export const getGoogleReserveQueryParameters = () => {
  const parameters = getQueryStringParameters();
  const googleReserveQueryParamsArray = parameters.filter(param =>
    Object.values(GoogleReserveQueryParamKeys).includes(param.key),
  );
  return Object.fromEntries(googleReserveQueryParamsArray.map(item => [item.key, item.value]));
};

/* eslint-disable no-param-reassign */
/* eslint-disable import/prefer-default-export */
/**
 * getFieldPropertyValues returns object of property values for @propertyNames provided
 * as stored in the @propertyMap provided.
 *
 * @param {Map} propertyMap                 The property map containing the properties we want values for
 * @param {(string|string[])} propertyNames A string or array of strings of the name(s) of properties we want returned
 *                                          E.g.: ['label', 'makeRequired']
 * @param {object} [defaultValues={}]       An object in the shape of key:value pairs of propertyName:defaultValue
 *                                          E.g.: {label: 'someValue', makeRequired: false}
 * @return {object}                         {<key-for-each-@propertyNames>: {<value of any (*) type>}}
 */

const getFieldPropertyValues = (propertyMap, propertyNames, defaultValues = {}) => {
  let property;
  const values = {};
  if (typeof propertyNames === 'string') propertyNames = [propertyNames];
  propertyNames.forEach(propertyName => {
    try {
      property = propertyMap.get(propertyName);
      values[propertyName] = property.value;
    } catch (ex) {
      if (!property || !values[propertyName]) values[propertyName] = defaultValues[propertyName];
    }
  });
  return values;
};

export const getOptionValue = (option, key) => {
  if (option === undefined) {
    return '';
  }
  return option.hasOwnProperty(key) ? option[key] : option;
};

/**
 * getOptionsHashMap: Converts the `options` property into a hash map in order to identify if
 *                    option values are repeated/duplicated. 
 * 
 * @param {*} options Array of primitive string values or objects. Example:
 *  [
 *    "Option 1",
 *    {
 *        "alt": "happy",
 *        "src": "https://url.cut.for.brevity.png",
 *        "width": "30",
 *        "height": "30"
 *    },
 *    "happy",
 *    "",
 *    ""
 *  ]
 *
 * @returns Hash map object with unique keys for each option "label" (primitive string value or option.alt property value).
 *          The numeric key within each label key is the index from the original options array where the element is located.
 *  {
 *    "Option 1": {
 *        "0": "Option 1"
 *    },
 *    "happy": {
 *        "1": {
 *            "alt": "happy",
 *            "src": "https://url.cut.for.brevity.png",
 *            "width": "30",
 *            "height": "30"
 *        },
 *        "2": "happy"
 *    },
 *    "": {
 *        "3": "",
 *        "4": ""
 *    }
 *  }
 */
export const getOptionsHashMap = (options) => {
  let optionsHash = {};
  options.forEach((option, index) => {
    const optionValue = getOptionValue(option, 'alt');
    if (!optionsHash[optionValue]) {
      optionsHash[optionValue] = {
        [index]: option,
      }
    } else {
      optionsHash[optionValue][index] = option;
    }
  });

  return optionsHash;
};

export const hasFieldByName = (field, fieldName) => {
  const recurseHasField = (field, fieldName) => {
    if (fieldIsType(field, fieldName, 'display_name') || fieldIsType(field, fieldName, 'group_name')) {
      return true;
    }

    if (field.fields) {
      return some(field.fields, (childField) =>
        recurseHasField(childField, fieldName)
      );
    }

    return false;
  };
  return recurseHasField(field, fieldName);
};
